import type { ReactNode } from "react";
import { ExternalLink, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface HelpPopoverProps {
  /** The operator guide section this popover summarises, e.g. `/docs/vm-actions.html#reinstall`. */
  guideHref: string;

  /** The short version: what the action does, when to use it, what it never does. */
  children: ReactNode;

  /**
   * Extra classes for the trigger. A dialog footer needs none; a page's side panel passes `w-full`.
   */
  className?: string;

  /** Which edge of the trigger the popover lines up with. A right-hand panel passes `end`. */
  align?: "start" | "end";
}

/**
 * "How this works" for a dialog footer: the short version opened in place, the full guide one click
 * further in its own tab. The same shape the recovery page and the retry dialog carry inline.
 */
export function HelpPopover({
  guideHref,
  children,
  className,
  align = "start",
}: HelpPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("sm:mr-auto", className)}
        >
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side="top"
        className="max-h-[70vh] w-96 space-y-3 overflow-y-auto text-sm"
      >
        {children}

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href={guideHref} target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/** One titled block inside a `HelpPopover`. */
export function HelpSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="font-medium">{title}</p>
      <div className="space-y-1 text-muted-foreground">{children}</div>
    </div>
  );
}
